import { baseApi } from "@/shared/api";
import type {
  AnnualPhotoPoolItem,
  AnnualReport,
  AnnualReportCandidate,
  AnnualReportCreatePayload,
  AnnualReportListItem,
  AnnualReportPayload,
} from "../types";

interface GenerateResponse {
  url: string;
  report: AnnualReport;
}

interface PhotoPoolResponse {
  items: AnnualPhotoPoolItem[];
}

export const annualReportsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAnnualReports: builder.query<AnnualReportListItem[], number | void>({
      query: (year) => ({
        url: `/api/projects/annual-reports/${typeof year === "number" ? `?year=${year}` : ""}`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: [{ type: "AnnualReports", id: "LIST" }],
    }),
    getAnnualReport: builder.query<AnnualReport, number>({
      query: (id) => ({
        url: `/api/projects/annual-reports/${id}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, id) => [{ type: "AnnualReports", id }],
    }),
    getAnnualReportData: builder.query<AnnualReportPayload, number>({
      query: (id) => ({
        url: `/api/projects/annual-reports/${id}/data/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, id) => [{ type: "AnnualReports", id: `DATA-${id}` }],
    }),
    getAnnualReportCandidates: builder.query<AnnualReportCandidate[], number>({
      query: (year) => ({
        url: `/api/projects/annual-reports/candidates/?year=${year}`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, year) => [
        { type: "AnnualReports", id: `CANDIDATES-${year}` },
      ],
    }),
    createAnnualReport: builder.mutation<AnnualReport, AnnualReportCreatePayload>({
      query: (body) => ({
        url: "/api/projects/annual-reports/",
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "AnnualReports", id: "LIST" }],
    }),
    updateAnnualReport: builder.mutation<
      AnnualReport,
      { id: number; body: Partial<AnnualReport> }
    >({
      query: ({ id, body }) => ({
        url: `/api/projects/annual-reports/${id}/`,
        method: "PATCH",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "AnnualReports", id },
        { type: "AnnualReports", id: `DATA-${id}` },
        { type: "AnnualReports", id: "LIST" },
      ],
    }),
    deleteAnnualReport: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/projects/annual-reports/${id}/`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "AnnualReports", id: "LIST" }],
    }),
    generateAnnualReport: builder.mutation<GenerateResponse, number>({
      query: (id) => ({
        url: `/api/projects/annual-reports/${id}/generate/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "AnnualReports", id },
        { type: "AnnualReports", id: "LIST" },
      ],
    }),
    getAnnualPhotoPool: builder.query<PhotoPoolResponse, number>({
      query: (id) => ({
        url: `/api/projects/annual-reports/${id}/photo-pool/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, id) => [{ type: "AnnualReports", id: `PHOTOS-${id}` }],
    }),
    getAnnualPhotoPoolPreview: builder.query<
      PhotoPoolResponse,
      { year: number; projectIds: number[] }
    >({
      query: ({ year, projectIds }) => ({
        url: `/api/projects/annual-reports/photo-pool-preview/?year=${year}&project_ids=${projectIds.join(",")}`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, { year }) => [
        { type: "AnnualReports", id: `PHOTOS-PREVIEW-${year}` },
      ],
    }),
    uploadAnnualPhotos: builder.mutation<PhotoPoolResponse, { id: number; files: File[] }>({
      query: ({ id, files }) => {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));
        return {
          url: `/api/projects/annual-reports/${id}/photos/`,
          method: "POST",
          body: formData,
          credentials: "include" as const,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: "AnnualReports", id: `PHOTOS-${id}` }],
    }),
    deleteAnnualPhoto: builder.mutation<AnnualReport, { id: number; key: string }>({
      query: ({ id, key }) => ({
        url: `/api/projects/annual-reports/${id}/photos/delete/?key=${encodeURIComponent(key)}`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "AnnualReports", id },
        { type: "AnnualReports", id: `DATA-${id}` },
        { type: "AnnualReports", id: `PHOTOS-${id}` },
      ],
    }),
    uploadAnnualStagingPhotos: builder.mutation<PhotoPoolResponse, { files: File[] }>({
      query: ({ files }) => {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));
        return {
          url: "/api/projects/annual-reports/photo-staging/",
          method: "POST",
          body: formData,
          credentials: "include" as const,
        };
      },
    }),
    deleteAnnualStagedPhoto: builder.mutation<void, { key: string }>({
      query: ({ key }) => ({
        url: `/api/projects/annual-reports/photo-staging/delete/?key=${encodeURIComponent(key)}`,
        method: "DELETE",
        credentials: "include" as const,
      }),
    }),
  }),
});

export const {
  useGetAnnualReportsQuery,
  useGetAnnualReportQuery,
  useGetAnnualReportDataQuery,
  useGetAnnualReportCandidatesQuery,
  useCreateAnnualReportMutation,
  useUpdateAnnualReportMutation,
  useDeleteAnnualReportMutation,
  useGenerateAnnualReportMutation,
  useGetAnnualPhotoPoolQuery,
  useGetAnnualPhotoPoolPreviewQuery,
  useUploadAnnualPhotosMutation,
  useDeleteAnnualPhotoMutation,
  useUploadAnnualStagingPhotosMutation,
  useDeleteAnnualStagedPhotoMutation,
} = annualReportsApi;
